import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Story from "@/components/Story";
import Property from "@/components/Property";
import Experiences from "@/components/Experiences";
import Gallery from "@/components/Gallery";
import Testimonials from "@/components/Testimonials";
import Location from "@/components/Location";
import Booking from "@/components/Booking";
import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import SocialIcons from "@/components/SocialIcons";
import { PROPERTIES } from "@/lib/properties";
import { useLang } from "@/contexts/LanguageContext";
import { ArrowLeft } from "lucide-react";

// Per-property SEO metadata (Spanish)
const PROPERTY_SEO = {
  casita: {
    title: "La Casita Pura Vida — Casa frente al mar en Barra de Galindo, Tuxpan",
    description: "Casa completa para familias en Barra de Galindo, Tuxpan Veracruz. 3 recámaras, cocina completa, playa privada. Reserva directo y ahorra 10% vs Airbnb.",
    keywords: "casa frente al mar tuxpan, hospedaje familiar barra de galindo, renta casa playa veracruz, casa completa tuxpan, la casita pura vida",
    image: "https://puravidatuxpan.com/api/img/hospedaje-tuxpan-veracruz-barra-galindo-casa-frente-al-mar-casita-01.jpg",
    imageAlt: "Casa frente al mar en Barra de Galindo, Tuxpan Veracruz — La Casita Pura Vida",
  },
  loft: {
    title: "El Loft Pura Vida — Loft frente al mar para parejas en Tuxpan, Veracruz",
    description: "Loft íntimo para parejas frente al mar en Barra de Galindo, Tuxpan Veracruz. 1 recámara, cocina equipada, playa privada. Reserva directo y ahorra 10% vs Airbnb.",
    keywords: "loft frente al mar tuxpan, hospedaje parejas barra de galindo, renta loft veracruz, escapada romántica tuxpan, el loft pura vida",
    image: "https://puravidatuxpan.com/api/img/loft-frente-al-mar-tuxpan-veracruz-barra-galindo-parejas-01.jpg",
    imageAlt: "Loft frente al mar en Barra de Galindo, Tuxpan Veracruz — El Loft Pura Vida",
  },
};

export default function PropertyDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const property = PROPERTIES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [property]);

  if (!property) return <Navigate to="/" replace />;

  const seo = PROPERTY_SEO[property.id] || {
    title: property.name,
    description: property.short_desc,
  };

  return (
    <div data-testid={`property-page-${property.id}`} className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <PageSEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={`/villas/${slug}`}
        image={seo.image}
        imageAlt={seo.imageAlt}
        type="lodging.hotel"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          name: property.name,
          description: seo.description,
          url: `https://puravidatuxpan.com/villas/${slug}`,
          image: seo.image,
          address: {
            "@type": "PostalAddress",
            addressLocality: "Barra de Galindo",
            addressRegion: "Veracruz",
            addressCountry: "MX",
          },
          numberOfRooms: property.bedrooms,
          occupancy: {
            "@type": "QuantitativeValue",
            maxValue: property.capacity,
          },
        }}
      />
      <Header variant="property" propertyName={property.short_name} currentPropertyId={property.id} />
      <main>
        <Hero property={property} />
        <Story property={property} />
        <Property property={property} />
        <SocialIcons
          variant="section"
          testPrefix={`prop-${property.id}-after-desc`}
          airbnbUrl={property.airbnb_url}
          whatsappMessage={
            lang === "es"
              ? `Hola, me interesa reservar ${property.name}`
              : `Hi, I want to book ${property.name}`
          }
        />
        <Experiences property={property} />
        <Gallery property={property} />
        <Testimonials propertyId={property.id} />
        <Location />
        <Booking property={property} />
        <FAQ />
        <SocialIcons
          variant="section"
          testPrefix={`prop-${property.id}-end`}
          airbnbUrl={property.airbnb_url}
          whatsappMessage={
            lang === "es"
              ? `Hola, me interesa reservar ${property.name}`
              : `Hi, I want to book ${property.name}`
          }
        />
      </main>

      <div className="bg-stone-100 py-12 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between flex-wrap gap-4">
          <Link
            to="/"
            data-testid="back-to-villas"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-stone-700 hover:text-[#E07A5F] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("hero.back_to_villas")}
          </Link>
          <div className="flex gap-3">
            {Object.values(PROPERTIES)
              .filter((p) => p.id !== property.id)
              .map((p) => (
                <Link
                  key={p.id}
                  to={`/villas/${p.slug}`}
                  data-testid={`other-property-${p.id}`}
                  className="text-sm border border-stone-900 px-5 py-2 hover:bg-stone-900 hover:text-stone-50 transition-colors"
                >
                  {t("villas_home.view_property")} {p.name} →
                </Link>
              ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
